import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { getSupabaseClient } from "../providers/supabase/supabase";

/**
 * Settings → Email capture - the review queue for BCC/forward capture
 * (decision #46). A sender matching exactly one contact never lands here -
 * it's already on the timeline. Everyone else waits here for a human to
 * pick "create contact + log" or "discard"; nothing touches the spine
 * until that choice (promote_pending_email_capture is the one sanctioned
 * path onto activity_events, decision #47's migration).
 */

interface PendingCaptureRow {
  id: string;
  from_address: string;
  to_addresses: string[];
  cc_addresses: string[];
  subject: string | null;
  body_preview: string | null;
  received_at: string;
  status: "new" | "resolved" | "discarded";
  resolved_contact_id: number | null;
  resolved_at: string | null;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const EmailCapturePage = () => {
  const [rows, setRows] = useState<PendingCaptureRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [linkEmail, setLinkEmail] = useState("");

  const load = useCallback(async () => {
    const { data, error: err } = await getSupabaseClient()
      .from("email_capture_pending")
      .select("*")
      .order("received_at", { ascending: false })
      .limit(200);
    if (err) setError(err.message);
    else setRows((data ?? []) as PendingCaptureRow[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const discard = async (row: PendingCaptureRow) => {
    setBusyId(row.id);
    setError(null);
    const { error: err } = await getSupabaseClient()
      .from("email_capture_pending")
      .update({ status: "discarded", resolved_at: new Date().toISOString() })
      .eq("id", row.id);
    if (err) setError(err.message);
    setBusyId(null);
    await load();
  };

  const createContact = async (row: PendingCaptureRow) => {
    setBusyId(row.id);
    setError(null);
    const { error: err } = await getSupabaseClient().rpc(
      "promote_pending_email_capture",
      { pending_id: row.id },
    );
    if (err) setError(err.message);
    setBusyId(null);
    await load();
  };

  const linkExisting = async (row: PendingCaptureRow) => {
    const email = linkEmail.trim().toLowerCase();
    if (!email) return;
    setBusyId(row.id);
    setError(null);
    const client = getSupabaseClient();
    const { data, error: findErr } = await client
      .from("contacts")
      .select("id")
      .contains("email_jsonb", JSON.stringify([{ email }]));
    if (findErr) {
      setError(findErr.message);
      setBusyId(null);
      return;
    }
    const matches = (data ?? []) as { id: number }[];
    if (matches.length !== 1) {
      setError(
        matches.length === 0
          ? `No contact found with the email "${email}".`
          : `Multiple contacts share the email "${email}" - link from the contact record instead.`,
      );
      setBusyId(null);
      return;
    }
    const { error: rpcErr } = await client.rpc("promote_pending_email_capture", {
      pending_id: row.id,
      contact_id: matches[0]!.id,
    });
    if (rpcErr) setError(rpcErr.message);
    setBusyId(null);
    setLinkingId(null);
    setLinkEmail("");
    await load();
  };

  const open = (rows ?? []).filter((r) => r.status === "new");
  const closed = (rows ?? []).filter((r) => r.status !== "new");

  const statusChip = (r: PendingCaptureRow) => (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
        r.status === "new"
          ? "bg-[#c9a75a]/15 text-[#a3843c]"
          : r.status === "resolved"
            ? "bg-[#6a8f6b]/15 text-[#6a8f6b]"
            : "bg-muted text-muted-foreground"
      }`}
    >
      {r.status === "new" ? "Needs review" : r.status === "resolved" ? "Logged" : "Discarded"}
    </span>
  );

  const captureRow = (r: PendingCaptureRow) => (
    <div key={r.id} className="flex flex-col gap-2 py-3 border-t border-border first:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{r.from_address}</span>
        {statusChip(r)}
        <span className="text-xs text-muted-foreground ml-auto">{fmtDate(r.received_at)}</span>
      </div>
      <p className="text-sm text-muted-foreground">
        {r.subject || "(no subject)"}
        {r.body_preview ? ` — ${r.body_preview.slice(0, 140)}` : ""}
      </p>
      {r.status === "new" &&
        (linkingId === r.id ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={linkEmail}
              onChange={(e) => setLinkEmail(e.target.value)}
              placeholder="existing contact's email"
              className="w-56"
            />
            <Button size="sm" disabled={busyId === r.id} onClick={() => void linkExisting(r)}>
              {busyId === r.id ? "Linking…" : "Link"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setLinkingId(null);
                setLinkEmail("");
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={busyId === r.id}
              onClick={() => void createContact(r)}
            >
              {busyId === r.id ? "Logging…" : "Create contact + log"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLinkingId(r.id)}
            >
              Link to existing contact
            </Button>
            <Button variant="ghost" size="sm" onClick={() => void discard(r)}>
              Discard
            </Button>
          </div>
        ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="text-sm text-[#c25d5d]">
          {error}
        </p>
      )}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-1">Needs review</h3>
          <p className="text-xs text-muted-foreground mb-2">
            Mail sent to your log address that didn't match exactly one
            contact waits here. Nothing is added to a timeline until you
            choose - discarding keeps it out of your data entirely.
          </p>
          {rows === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : open.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing needs review.</p>
          ) : (
            open.map(captureRow)
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-2">History</h3>
          {rows === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : closed.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing resolved yet.</p>
          ) : (
            closed.map(captureRow)
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground max-w-prose">
        Mail matching exactly one contact's email address logs automatically
        and never appears here. Capture is BCC/forward only - there is no
        inbox.
      </p>
    </div>
  );
};
